import { TrackingEvent } from '../types/Exponea.ts'
import {
    FeedbackAction,
    NPS_DETRACTOR_MAX_SCORE,
    NPS_MAX_SCORE,
    NPS_MIN_SCORE,
    NPS_PASSIVE_MAX_SCORE,
    NpsBand
} from '../types/Feedback.ts'

/**
 * How long an NPS answer suppresses the next NPS ask. Mirrors the 180-day window the
 * Bloomreach NPS scenarios use, except this one keys off answers rather than sends.
 */
export const NPS_REASK_COOLDOWN_DAYS = 180

/**
 * True when the user already answered an NPS question inside the cooldown window, in
 * which case we should not trigger another NPS ask for them.
 */
export function hasGivenNpsWithinDays(
    feedbackMetas: { createdAt?: unknown; nps?: number }[] | undefined,
    days: number = NPS_REASK_COOLDOWN_DAYS
): boolean {
    if (!feedbackMetas?.length) return false

    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000

    return feedbackMetas.some((meta) => {
        if (meta.nps === undefined || meta.nps === null) return false

        const createdAt = toMillis(meta.createdAt)
        return createdAt !== undefined && createdAt >= cutoff
    })
}

function toMillis(value: unknown): number | undefined {
    if (!value) return undefined
    if (value instanceof Date) return value.getTime()

    const record = value as { toDate?: () => Date; seconds?: number; _seconds?: number }
    if (typeof record.toDate === 'function') return record.toDate().getTime()
    if (typeof record.seconds === 'number') return record.seconds * 1000
    if (typeof record._seconds === 'number') return record._seconds * 1000

    const parsed = new Date(value as string).getTime()
    return Number.isNaN(parsed) ? undefined : parsed
}

/**
 * Official NPS banding: 0-6 detractors, 7-8 passives, 9-10 promoters.
 */
export function getNpsBand(score: number): NpsBand {
    if (score <= NPS_DETRACTOR_MAX_SCORE) return NpsBand.DETRACTOR
    if (score <= NPS_PASSIVE_MAX_SCORE) return NpsBand.PASSIVE
    return NpsBand.PROMOTER
}

/** Anything <= 6 counts as an unsatisfied customer. */
export function isNpsSatisfied(score: number): boolean {
    return score > NPS_DETRACTOR_MAX_SCORE
}

export function isValidNpsScore(score: unknown): score is number {
    return typeof score === 'number' && Number.isInteger(score) && score >= NPS_MIN_SCORE && score <= NPS_MAX_SCORE
}

/**
 * Reads the NPS score out of an email rating code. The Bloomreach NPS mails link
 * back with the raw score, older mails still use the 1-5 `FS7x` star codes, so
 * those are upscaled onto the 0-10 range for continuity.
 */
/**
 * Reads the NPS score out of a landing-page query string. Accepts both `rc=7` and the
 * `utm_term=NPS07` form the current Bloomreach NPS mail templates use, so the mails can
 * be repointed here without changing their tracking convention.
 */
export function readNpsScoreFromQuery(query: Record<string, unknown>): number | undefined {
    const fromRc = translateCodeToNpsScore(readParam(query, 'rc'))
    if (fromRc !== undefined) return fromRc

    const utmTerm = readParam(query, 'utm_term')
    if (!utmTerm) return undefined

    const match = /^NPS(\d{1,2})$/i.exec(utmTerm.trim())
    if (!match) return undefined

    const parsed = Number(match[1])
    return isValidNpsScore(parsed) ? parsed : undefined
}

/**
 * Reads a query param, tolerating the `amp;` prefix that appears when an e-mail template
 * escapes the `&` separator as `&amp;` - the link still works, but the parameter would
 * otherwise arrive named `amp;utm_term` and the score would be silently lost.
 */
function readParam(query: Record<string, unknown>, name: string): string | undefined {
    return asString(query[name]) ?? asString(query[`amp;${name}`])
}

function asString(value: unknown): string | undefined {
    if (typeof value === 'string') return value
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
    return undefined
}

export function translateCodeToNpsScore(ratingCode?: string): number | undefined {
    if (!ratingCode) return undefined

    const direct = Number(ratingCode)
    if (isValidNpsScore(direct)) return direct

    const stars = translateCodeToFeedbackStars(ratingCode)
    if (!stars) return undefined

    // 1-5 stars -> 0-10 so legacy links keep landing in a sensible band
    return Math.round(((stars - 1) / 4) * NPS_MAX_SCORE)
}

/**
 * Maps the tracking event carried inside a feedback action token to its feedback action.
 *
 * Action tokens encode the raw `TrackingEvent` value (e.g. `mentoring_prijaty_feedback_mentor`),
 * so those are the names that must match. The hyphenated aliases are kept only for tokens that
 * may still be sitting in old mailboxes.
 *
 * Returns `undefined` for an unrecognised event rather than guessing - a mis-filed NPS score is
 * invisible in reporting, a rejected link is not.
 */
export function getFeedbackActionForTrackingEvent(event: string): FeedbackAction | undefined {
    switch (event) {
        case TrackingEvent.PROJECT_COOPERATION_FEEDBACK_ORG:
        case 'project-cooperation-feedback-org':
            return FeedbackAction.PROJECT_COOPERATION_ORG
        case TrackingEvent.PROJECT_COOPERATION_FEEDBACK_VOLUNTEER:
        case 'project-cooperation-feedback-volunteer':
            return FeedbackAction.PROJECT_COOPERATION_VOLUNTEER
        case TrackingEvent.MENTORING_COOPERATION_FEEDBACK_MENTOR:
        case 'mentoring-cooperation-feedback-mentor':
            return FeedbackAction.MENTORING_COOPERATION_MENTOR
        case TrackingEvent.MENTORING_COOPERATION_FEEDBACK_MENTEE:
        case 'mentoring-cooperation-feedback-mentee':
            return FeedbackAction.MENTORING_COOPERATION_MENTEE
        case TrackingEvent.PROJECT_CREATED:
            return FeedbackAction.PROJECT_CREATE
        default:
            return undefined
    }
}

/**
 * Translates rating code to stars
 */
export function translateCodeToFeedbackStars(ratingCode?: string): number {
    if (!ratingCode) {
        return 0
    }

    switch (ratingCode) {
        case 'FS75':
        case 'excellent':
            return 5
        case 'FS74':
        case 'very-good':
            return 4
        case 'FS73':
        case 'good':
            return 3
        case 'FS72':
        case 'fair':
            return 2
        case 'FS71':
        case 'poor':
            return 1
        default:
            return 0
    }
}